/**
 * EditGateActions — the ONE shared Edit / Save+Cancel button set for CONFIG
 * PANELS driven by `useEditGate` (components/confirm).  Renders into the
 * AppToolbar `left` slot so every settings panel gets the SAME view-then-edit
 * controls with the SAME `.ca-btn` styling and glyphs as the rest of the app:
 *
 *   • READ-ONLY  → [ ✎ Edit ]
 *   • EDITING    → [ 💾 Save ] [ ↩︎ Cancel ]
 *
 * Glyphs match the BO UI Standard prototype (blue ✎ Edit, solid 💾 Save,
 * ↩︎ Cancel) — identical to SettingsDetailPage's action bar.
 *
 *   const gate = useEditGate();
 *   <AppToolbar left={
 *     <EditGateActions gate={gate} saving={saving} disabled={loading}
 *       onSave={() => gate.save({ formName, save: persistAll, reload })}
 *       onCancel={() => gate.cancel(revertToLoaded)} />
 *   } onRefresh={…} onExport={…} />
 */
import { BusyIcon } from '../loading';
import { useHotkeys, formatCombo, effectiveCombo } from '../hotkeys';
import type { EditGate } from '../confirm/useEditGate';

export interface EditGateActionsProps {
  gate: EditGate;
  /** Invoked when the user clicks Save (call `gate.save({...})`). */
  onSave: () => void;
  /** Invoked when the user clicks Cancel (call `gate.cancel(revert)`). */
  onCancel: () => void;
  /** True while a save is in flight — shows the shared spinner, blocks re-click. */
  saving?: boolean;
  /** Disable Edit (e.g. while the panel is still loading). */
  disabled?: boolean;
  /** Override the Edit button label (default "Edit"). */
  editLabel?: string;
}

export function EditGateActions({
  gate, onSave, onCancel, saving = false, disabled = false, editLabel = 'Edit',
}: EditGateActionsProps) {
  // Keyboard shortcuts for the shared edit-gate — bound for EVERY config panel
  // that renders this component, with ZERO per-page code (CLAUDE.md standing
  // rule 14, §18.6, ADR-0013). The hook is UNCONDITIONAL (before the early
  // return); only the ARRAY is gated by the edit state, so read-only shows
  // Edit (Alt+E) and editing shows Save (Ctrl+S) + Cancel (Esc). Esc is
  // allowInInput:false because Cancel discards edits — it must not fire while
  // the user is typing in a field. Each mirrors the visible button exactly
  // (same handler + same disabled gate).
  useHotkeys(
    gate.editing
      ? [
          { combo: 'mod+s', label: 'Save', group: 'Detail', order: 1, run: () => onSave(), disabled: saving || disabled },
          { combo: 'esc', label: 'Cancel', group: 'Detail', order: 2, run: () => onCancel(), disabled: saving, allowInInput: false },
        ]
      : [
          { combo: 'alt+e', label: editLabel, group: 'Detail', order: 1, run: () => gate.beginEdit(), disabled },
        ],
  );

  if (!gate.editing) {
    return (
      <button className="ca-btn" onClick={gate.beginEdit} disabled={disabled} title={`${editLabel} (${formatCombo(effectiveCombo('alt+e'))})`}>
        <span className="tb-i tb-i-blue">✎</span> {editLabel}
      </button>
    );
  }
  return (
    <>
      <button className="ca-btn ca-btn-solid" onClick={onSave} disabled={saving || disabled} title={`Save (${formatCombo(effectiveCombo('mod+s'))})`}>
        {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
      </button>
      <button className="ca-btn" onClick={onCancel} disabled={saving} title={`Cancel (${formatCombo(effectiveCombo('esc'))})`}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </>
  );
}
